/**
 * @file   src\components\MemberWantAd\EditProfileModal.js
 * @brief  This file is responsible for editing agent profile.
 * @date   June, 2023
 * @author ZCO Engineer
 * @copyright (c) 2023, ZCO
 */
import React, { useState, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import { Button } from "react-bootstrap";
import TextBox from "../../Common/TextBox";
import {
  toTitleCase,
  getLocalStorage,
  setLocalStorage,
  trimString,
  formatPhoneNumber,
  removeLocalStorage,
} from "../../../helpers/common";
import {
  nameValidation,
  alphaNumericValidation,
  textValidations,
  licenseValidation,
  phoneNumberValidation,
} from "../../../helpers/validations";
import { editProfilePremiumAgent } from "../../../services/userprofileService";
import { toast } from "react-toastify";
import CheckBox from "../../Common/CheckBox";
import {
  AGENT_USER_FREE_PLAN,
  BOTH_TITLE,
  PROPERTY_MANAGER,
  REAL_ESTATE_AGENT,
  USER_INFO_KEY,
  USER_TYPE_AGENT,
} from "../../../constants/common";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useQueryClient } from "@tanstack/react-query";
import { fetchUserDetails } from "../../../actions/userprofile";
import { getGeoLocation } from "../../../services/commonServices";
import { capitalizeString } from "../../../actions/common";
import useIsMobile from "@/hooks/useIsMobile";

const EditProfileModal = (props) => {
  const { userDetails, editAgentProfile, isFromUpgradeToAgent, selectedPlan, roster } =
    props;
  const rosterBrokerage = roster?.linked ? roster.brokerage : null;
  const rosterLicense = roster?.linked ? roster.license : null;
  const rosterNetwork = roster?.network || null;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [firstName, setFirstName] = useState(userDetails?.FirstName);
  const [lastName, setLastName] = useState(userDetails?.LastName);
  const [company, setCompany] = useState(userDetails?.Company);
  const [location, setLocation] = useState(userDetails?.Location);
  const [locationError, setLocationError] = useState("");
  const [firstNameError, setFirstNameError] = useState("");
  const [lastNameError, setLastNameError] = useState("");
  const [companyError, setCompanyError] = useState("");
  const locationString = getLocalStorage("LocationString");
  const [locationList, setLocationList] = useState([]);
  const [formattedAddress, setFormattedAddress] = useState();
  const [license, setLicense] = useState(userDetails?.LicenseNo);
  const [licenseError, setLicenseError] = useState("");
  const [isRealEstateAgent, setIsRealEstateAgent] = useState(
    userDetails?.Agent_Title === REAL_ESTATE_AGENT ||
      userDetails?.Agent_Title === BOTH_TITLE
      ? true
      : false,
  );
  const [isPropertyManager, setIsPropertyManager] = useState(
    userDetails?.Agent_Title === PROPERTY_MANAGER ||
      userDetails?.Agent_Title === BOTH_TITLE
      ? true
      : false,
  );

  const [phone, setPhone] = useState(userDetails?.Phone);
  const [phoneError, setPhoneError] = useState("");

  useEffect(() => {
    setPhone(userDetails?.Phone);
    setFirstName(userDetails?.FirstName);
    setLastName(userDetails?.LastName);
    setCompany(userDetails?.Company);
    setLocation(userDetails?.Location);
    setLicense(userDetails?.LicenseNo);
  }, [userDetails]);

  useEffect(() => {
    if (rosterBrokerage) setCompany(rosterBrokerage);
    if (rosterLicense) setLicense(rosterLicense);
  }, [rosterBrokerage, rosterLicense]);

  const getAddresseDetails = async ({ eventType, reqPayload }) => {
    await getGeoLocation(reqPayload).then((data) => {
      const results = _.get(data, "results");
      if (results?.length > 0) {
        const formatted_address = _.get(results?.[0], "formatted_address", "");
        if (results?.length > 0 && formatted_address !== "United States") {
          if (locationString) {
            handleItemSelection(results[0]);
            removeLocalStorage("LocationString");
          } else {
            setLocationList(results);
          }
        }
      }
    });
  };

  const handleItemSelection = async (address) => {
    let city, state, county;
    removeLocalStorage("searchedLocation");
    const geometry = _.get(address, "geometry", "");
    const locBounds = _.get(geometry, "location", "");
    const formatted_address = _.get(address, "formatted_address", "");
    const firstAddressComponent = _.get(address, "address_components", "");
    const stateSearched = firstAddressComponent.filter(function (ca) {
      return ca.types?.[0] === "administrative_area_level_1";
    });
    if (stateSearched.length) {
      state = _.get(stateSearched?.[0], "short_name", "");
    }
    const locality = firstAddressComponent.filter(function (ca) {
      return ca.types?.[0] === "locality";
    });
    if (locality.length) {
      city = _.get(locality?.[0], "long_name", "");
    }
    const countyList = firstAddressComponent.filter(function (ca) {
      return ca.types?.[0] === "administrative_area_level_2";
    });

    if (countyList.length) {
      county = _.get(countyList?.[0], "short_name", "");
    }
    if (city != undefined) {
      setFormattedAddress(city + ", " + state);
    } else {
      setFormattedAddress(formatted_address);
    }
    setLocation(city != undefined ? city + ", " + state : formatted_address);
    clearAddressList();
  };

  const handleLocationChange = (val) => {
    const value = capitalizeString(val);
    const reqPayload = `address=${value}&components=country:US`;
    if (value.trim() !== "") {
      getAddresseDetails({ reqPayload: reqPayload });
    } else {
      clearAddressList();
    }
    if (value && value.trim() !== "") {
      setLocation(value);
    } else {
      setLocation(value);
    }
  };

  const clearAddressList = () => {
    setLocationList([]);
  };

  /**
   * To handle first name field changes
   * @param {*} name
   */
  const handleFirstName = (name) => {
    setFirstNameError("");
    setFirstName(name);
    if (name.trim() === "") {
      setFirstNameError("Enter First Name");
    } else if (!nameValidation(name.trim())) {
      setFirstNameError("Invalid input");
    }
  };

  /**
   * To handle last name field changes
   * @param {*} name
   */
  const handleLastName = (name) => {
    setLastNameError("");
    setLastName(name);
    if (name.trim() === "") {
      setLastNameError("Enter Last Name");
    } else if (!nameValidation(name.trim())) {
      setLastNameError("Invalid input");
    }
  };

  /**
   * To handle company field changes
   * @param {*} name
   */
  const handleCompany = (name) => {
    setCompanyError("");
    setCompany(name);
    if (name.trim() === "") {
      setCompanyError("Enter Company");
    } else if (
      !/^[a-zA-Z0-9\s&]+$/.test(name.trim()) ||
      /^[0-9&]+$/.test(name.trim())
    ) {
      setCompanyError("Invalid input");
    }
  };

  /**
   * To handle  location field changes
   * @param {*} name
   */

  /**
   * To handle license field changes
   * @param {*} name
   */
  const handleLicenseChange = (name) => {
    setLicenseError("");
    setLicense(name);
    if (name.trim() === "") {
      setLicenseError("Enter License Number");
    } else if (!licenseValidation(name.trim())) {
      //V2 change for license validation
      setLicenseError("Invalid input");
    }
  };

  /**
   * To handle phone number field changes
   * @param {*} value
   */

  const handlePhone = (value) => {
    setPhoneError("");
    if (trimString(value) !== "") {
      value = formatPhoneNumber(value);
      setPhone(value);
    } else {
      setPhoneError("Enter Phone Number");
      setPhone("");
    }
  };

  /**
   * To validate phone number
   * @param {*} name
   */
  const validatePhoneNumber = () => {
    setPhoneError("");
    if (trimString(phone) !== "") {
      if (!phoneNumberValidation(phone.trim()) || phone === "000-000-0000") {
        setPhoneError("Enter Valid Phone Number");
      }
    } else {
      setPhoneError("Enter Phone Number");
      setPhone("");
    }
  };

  const handleCancel = () => {
    setFirstName(userDetails?.FirstName);
    setLastName(userDetails?.LastName);
    setCompany(userDetails?.Company);
    setLicense(userDetails?.LicenseNo);
    setLocation(userDetails?.Location);
    setIsPropertyManager(
      userDetails?.Agent_Title === PROPERTY_MANAGER ||
        userDetails?.Agent_Title === BOTH_TITLE
        ? true
        : false,
    );
    setIsRealEstateAgent(
      userDetails?.Agent_Title === REAL_ESTATE_AGENT ||
        userDetails?.Agent_Title === BOTH_TITLE
        ? true
        : false,
    );
    setPhone(userDetails?.Phone);
    setPhoneError("");
    setLicenseError("");
    setFirstNameError("");
    setLastNameError("");
    setCompanyError("");
    setLocationError("");
    props.onHide();
  };

  const getTitleOf = () => {
    if (isPropertyManager && isRealEstateAgent) {
      return 3;
    } else if (isPropertyManager) {
      return 2;
    } else {
      return 1;
    }
  };
  /**
   * To handle submit changes
   */
  const handleSubmit = async () => {
    // The endpoint overwrites the whole row, and this sheet no longer shows the
    // photo, bio, links or testimonials. Re-read them at submit so a bio saved a
    // moment ago in BioStudio is not overwritten by this sheet's older props.
    const userDetailsKey = ["userDetails"];
    try {
      if (queryClient.isFetching({ queryKey: userDetailsKey }) > 0) {
        await queryClient.refetchQueries({ queryKey: userDetailsKey });
      }
    } catch (e) {
      // Fall back to whatever is cached; the save itself still reports its own errors.
    }
    const cachedPlans = queryClient.getQueryData(userDetailsKey)?.result;
    const latest = (Array.isArray(cachedPlans) && cachedPlans[0]) || userDetails;
    const data = {
      FirstName: firstName,
      LastName: lastName,
      // MLS roster values win: Cancel or a refetch can reset local state to the
      // stored values while the read-only roster values are what the sheet shows.
      Company: rosterBrokerage || company,
      About: latest?.Bio,
      Img: latest?.Img,
      Title: getTitleOf(),
      Location: location,
      LicenseNo: rosterLicense || license,
      Phone: phone,
      facebookUrl: latest?.facebookUrl,
      instagramUrl: latest?.instagramUrl,
      websiteUrl: latest?.websiteUrl,
      youtubeUrl: latest?.youtubeUrl,
      clientTestimonial: latest?.clientTestimonial,
      logoUrl: latest?.logoUrl,
    };
    await editProfilePremiumAgent(data)
      .then((response) => {
        if (response.errorCode == 0) {
          toast.success("User's profile updated successfully.");
          let userInfo = getLocalStorage(USER_INFO_KEY);
          userInfo.userInfo["firstName"] = firstName;
          userInfo.userInfo["lastName"] = lastName;
          userInfo.userInfo["name"] = firstName + " " + lastName;
          userInfo.userInfo["userType"] = USER_TYPE_AGENT;
          setLocalStorage(USER_INFO_KEY, userInfo);
          // Update the shared user-profile store so the header reflects changes immediately.
          dispatch(fetchUserDetails());
          // Settings reads user details from React Query, not Redux, so the
          // cached entry has to be invalidated as well.
          queryClient.invalidateQueries({ queryKey: ["userDetails"] });
          queryClient.invalidateQueries({ queryKey: ["onboardingProgress"] });
          navigate("/settings");

          props.onHide();
        } else {
          toast.error(response.errorMessage);
        }
      })
      .catch((err) => {
        toast.error(err);
      });
  };
  /**
   * To handle validation
   * @returns
   */
  const checkValid = () => {
    if (
      userDetails?.PlanName === AGENT_USER_FREE_PLAN ||
      selectedPlan === "Lite"
    ) {
      if (
        location &&
        license &&
        company &&
        firstName &&
        lastName &&
        (isPropertyManager === true || isRealEstateAgent === true)
      ) {
        if (
          locationError ||
          licenseError ||
          companyError ||
          firstNameError ||
          lastNameError
        ) {
          return true;
        }
        return false;
      } else {
        return true;
      }
    } else {
      if (!phoneNumberValidation(phone)) {
        return true;
      } else if (
        firstName === userDetails?.FirstName &&
        lastName === userDetails?.LastName &&
        phone === userDetails?.phone &&
        company === userDetails?.Company &&
        license === userDetails?.LicenseNo &&
        location === userDetails?.Location &&
        (isPropertyManager ===
          (userDetails?.Agent_Title === PROPERTY_MANAGER ||
            userDetails?.Agent_Title === BOTH_TITLE)
          ? true
          : false) &&
        (isRealEstateAgent ===
          (userDetails?.Agent_Title === REAL_ESTATE_AGENT ||
            userDetails?.Agent_Title === BOTH_TITLE)
          ? true
          : false)
      ) {
        return true;
      } else if (
        firstNameError !== "" ||
        lastNameError !== "" ||
        companyError !== "" ||
        phoneError !== "" ||
        locationError !== "" ||
        licenseError !== ""
      ) {
        return true;
      } else if (
        firstName?.trim() === "" ||
        lastName?.trim() == "" ||
        company?.trim() == "" ||
        location?.trim() == "" ||
        (isPropertyManager === false && isRealEstateAgent === false)
      ) {
        return true;
      } else {
        return false;
      }
    }
  };

  const isNonPro =
    userDetails?.PlanName === AGENT_USER_FREE_PLAN || selectedPlan === "Lite";

  const isMobile = useIsMobile();

  return (
    <>
      <Modal
        show
        {...props}
        size="md"
        aria-labelledby="contained-modal-title-vcenter"
        centered
        className="modal-settings profile-sheet"
        backdrop="static"
        fullscreen={isMobile}
      >
        <Modal.Body className="agent-info-modal">
          {editAgentProfile || isFromUpgradeToAgent ? (
            <></>
          ) : (
            <button
              type="button"
              className="btn-close"
              aria-label="Close"
              onClick={handleCancel}
            ></button>
          )}
          <header className="profile-sheet__head">
            <h3 className="agent-modal-head mt-0">Edit details</h3>
            <p>
              Saved everywhere you show up: your Buyer Want{"\u2060"}-{"\u2060"}Listings, introductions
              and your Gitcha&nbsp;bio.
            </p>
          </header>

          <section className="profile-sheet__group">
            <p className="profile-sheet__label">Name</p>
            <div className="profile-sheet__card profile-sheet__pair">
              <div className="profile-sheet__field">
                <TextBox
                  id="fName"
                  label="First name"
                  name="firstName"
                  type="text"
                  maxLength={30}
                  value={toTitleCase(firstName)}
                  placeholder="First name"
                  labelClassName="agent-modal-label"
                  onChange={(e) => handleFirstName(e.target.value)}
                  errorClass="error-validation-msg"
                  errorMessage={firstNameError}
                />
              </div>
              <div className="profile-sheet__field">
                <TextBox
                  id="lName"
                  name="lastName"
                  label="Last name"
                  maxLength={30}
                  value={toTitleCase(lastName)}
                  type="text"
                  placeholder="Last name"
                  labelClassName="agent-modal-label"
                  errorClass="error-validation-msg"
                  onChange={(e) => handleLastName(e.target.value)}
                  errorMessage={lastNameError}
                />
              </div>
            </div>
          </section>

          <section className="profile-sheet__group">
            <p className="profile-sheet__label">Contact</p>
            <div className="profile-sheet__card">
              <div className="profile-sheet__field">
                <TextBox
                  id="phone"
                  name="phone"
                  label={
                    <>
                      Phone number{" "}
                      {isNonPro ? (
                        <div className="pro-badge d-inline-flex align-items-center ms-1">
                          pro
                        </div>
                      ) : (
                        <span>*</span>
                      )}
                    </>
                  }
                  labelClassName="agent-modal-label"
                  disabled={isNonPro}
                  type="text"
                  value={phone}
                  className={`${isNonPro ? "disabled-input" : ""}`}
                  errorClass="error-validation-msg"
                  onChange={(e) => handlePhone(e.target.value)}
                  onBlur={() => {
                    validatePhoneNumber();
                  }}
                  errorMessage={phoneError}
                  placeholder="Phone number"
                  maxLength={14}
                />
              </div>
            </div>
            <p className="profile-sheet__hint">The number agents call about your buyers.</p>
          </section>

          <section className="profile-sheet__group">
            <p className="profile-sheet__label">Where you work</p>
            <div className="profile-sheet__card">
              {rosterBrokerage ? (
                <div className="profile-sheet__managed">
                  <span className="profile-sheet__managed-label">Brokerage</span>
                  <span className="profile-sheet__managed-value">{rosterBrokerage}</span>
                </div>
              ) : (
                <div className="profile-sheet__field">
                  <TextBox
                    id="company"
                    name="company"
                    label="Brokerage"
                    type="text"
                    value={toTitleCase(company)}
                    labelClassName="agent-modal-label"
                    errorClass="error-validation-msg"
                    onChange={(e) => handleCompany(e.target.value)}
                    errorMessage={companyError}
                    placeholder="Brokerage name"
                    maxLength={40}
                  />
                </div>
              )}
              {rosterLicense ? (
                <div className="profile-sheet__managed">
                  <span className="profile-sheet__managed-label">License</span>
                  <span className="profile-sheet__managed-value">{rosterLicense}</span>
                </div>
              ) : (
                <div className="profile-sheet__field">
                  <TextBox
                    id="license"
                    name="license"
                    label="License number"
                    type="text"
                    value={license}
                    labelClassName="agent-modal-label"
                    errorClass="error-validation-msg"
                    onChange={(e) => handleLicenseChange(e.target.value)}
                    errorMessage={licenseError}
                    placeholder="License number"
                    maxLength={40}
                  />
                </div>
              )}
              <div className="profile-sheet__field position-relative">
                <TextBox
                  id="location"
                  name="location"
                  label="Location"
                  type="text"
                  value={toTitleCase(location)}
                  labelClassName="agent-modal-label"
                  errorClass="error-validation-msg"
                  onChange={(e) => handleLocationChange(e.target.value)}
                  errorMessage={locationError}
                  placeholder="City, State"
                  maxLength={40}
                />
                {locationList.length > 0 ? (
                  <div className="location-result-wrap-new">
                    <ul>
                      {locationList?.map((address, i) => (
                        <li key={i}>
                          <a role="button" href={void 0} onClick={() => handleItemSelection(address)}>
                            {_.get(address, "formatted_address", "")}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </div>
            </div>
            {rosterBrokerage || rosterLicense ? (
              <p className="profile-sheet__hint">
                Brokerage and license come from your {rosterNetwork || "MLS"} membership and update on their own when they
                change&nbsp;there.
              </p>
            ) : null}
          </section>

          <section className="profile-sheet__group">
            <p className="profile-sheet__label">You work as</p>
            <div className="profile-sheet__card profile-sheet__roles">
              <label className="form-check-label checkbox-label" htmlFor="realestate_agent">
                <CheckBox
                  className="form-check-input margin-top-zero"
                  type="checkbox"
                  id="realestate_agent"
                  checked={isRealEstateAgent}
                  onChange={() => setIsRealEstateAgent(!isRealEstateAgent)}
                />{" "}
                Real estate agent
              </label>
              <label className="form-check-label checkbox-label" htmlFor="property_manager">
                <CheckBox
                  className="form-check-input margin-top-zero"
                  type="checkbox"
                  id="property_manager"
                  checked={isPropertyManager}
                  onChange={() => setIsPropertyManager(!isPropertyManager)}
                />{" "}
                Property manager
              </label>
            </div>
          </section>

          <div className="modal-btn-wrap profile-sheet__foot">
            {editAgentProfile || isFromUpgradeToAgent ? (
              <></>
            ) : (
              <Button variant="" className="btn btn-tertiary btn-margin-fix me-3" onClick={handleCancel}>
                Cancel
              </Button>
            )}
            {/* TEMP: always allow Save changes, validation gate disabled.
                Restore: disabled={checkValid()} */}
            <Button variant="primary" disabled={false} onClick={handleSubmit}>
              Save changes
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};
export default EditProfileModal;
